import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getHealthProfile, updateHealthProfile, getMyProfile, updateMyProfile } from '../services/api';
import toast from 'react-hot-toast';

const CHRONIC_CONDITIONS = [
  'Hypertension', 'Type 2 Diabetes', 'Type 1 Diabetes', 'Asthma', 'COPD',
  'Coronary Artery Disease', 'Atrial Fibrillation', 'Heart Failure', 'Stroke',
  'Hypothyroidism', 'Hyperthyroidism', 'Chronic Kidney Disease', 'Sleep Apnea',
  'Depression', 'Anxiety', 'Arthritis', 'Osteoporosis',
];

const BLOOD_TYPES = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-', 'Unknown'];

export default function HealthProfilePage() {
  const { currentUser } = useAuth();
  const [profile, setProfile] = useState<any>({
    date_of_birth: '',
    sex: '',
    height_cm: '',
    blood_type: '',
    chronic_conditions: [],
    medications: [],
    allergies: [],
    family_history: { heart_disease: false, diabetes: false, cancer: false, stroke: false, hypertension: false },
    emergency_contact: { name: '', phone: '', relationship: '' },
  });
  const [weightGoalKg, setWeightGoalKg] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newMed, setNewMed] = useState('');
  const [newAllergy, setNewAllergy] = useState('');

  const load = useCallback(async () => {
    if (!currentUser) return;
    try {
      const token = await currentUser.getIdToken();
      const [healthRes, userRes] = await Promise.allSettled([
        getHealthProfile(token),
        getMyProfile(token),
      ]);
      if (healthRes.status === 'fulfilled' && healthRes.value.profile) {
        setProfile((prev: any) => ({ ...prev, ...healthRes.value.profile }));
      }
      if (userRes.status === 'fulfilled' && userRes.value.weight_goal_kg) {
        setWeightGoalKg(String(userRes.value.weight_goal_kg));
      }
    } catch {/* profile may not exist yet */} finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;
    setSaving(true);
    try {
      const token = await currentUser.getIdToken();
      const saves: Promise<any>[] = [updateHealthProfile(token, profile)];
      if (weightGoalKg !== '') {
        saves.push(updateMyProfile(token, { weight_goal_kg: parseFloat(weightGoalKg) }));
      }
      await Promise.all(saves);
      toast.success('Health profile saved!');
    } catch (e: any) {
      toast.error(e.message || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const toggleCondition = (c: string) => {
    setProfile((prev: any) => ({
      ...prev,
      chronic_conditions: prev.chronic_conditions.includes(c)
        ? prev.chronic_conditions.filter((x: string) => x !== c)
        : [...prev.chronic_conditions, c],
    }));
  };

  const addMed = () => {
    if (!newMed.trim()) return;
    setProfile((prev: any) => ({ ...prev, medications: [...(prev.medications || []), newMed.trim()] }));
    setNewMed('');
  };

  const removeMed = (i: number) => {
    setProfile((prev: any) => ({ ...prev, medications: prev.medications.filter((_: any, idx: number) => idx !== i) }));
  };

  const addAllergy = () => {
    if (!newAllergy.trim()) return;
    setProfile((prev: any) => ({ ...prev, allergies: [...(prev.allergies || []), newAllergy.trim()] }));
    setNewAllergy('');
  };

  const removeAllergy = (i: number) => {
    setProfile((prev: any) => ({ ...prev, allergies: prev.allergies.filter((_: any, idx: number) => idx !== i) }));
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64 text-gray-400">Loading profile...</div>;
  }

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Health Profile</h1>
        <p className="text-gray-500 text-sm mt-1">Your private medical profile — used to personalize AI insights.</p>
      </div>

      <form onSubmit={handleSave} className="space-y-6">
        {/* Basic info */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <h2 className="font-semibold text-gray-800 mb-4">Basic Information</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
              <input
                type="date"
                value={profile.date_of_birth || ''}
                onChange={(e) => setProfile((p: any) => ({ ...p, date_of_birth: e.target.value }))}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sex</label>
              <select
                value={profile.sex || ''}
                onChange={(e) => setProfile((p: any) => ({ ...p, sex: e.target.value }))}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="">Select</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other / Prefer not to say</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Height (cm)</label>
              <input
                type="number"
                value={profile.height_cm || ''}
                onChange={(e) => setProfile((p: any) => ({ ...p, height_cm: e.target.value }))}
                placeholder="175"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Blood Type</label>
              <select
                value={profile.blood_type || ''}
                onChange={(e) => setProfile((p: any) => ({ ...p, blood_type: e.target.value }))}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="">Unknown</option>
                {BLOOD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* Goals */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <h2 className="font-semibold text-gray-800 mb-1">Goals</h2>
          <p className="text-xs text-gray-500 mb-4">Used to show progress on your dashboard.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Target Weight (kg)</label>
              <input
                type="number"
                step="0.1"
                min="30"
                max="300"
                value={weightGoalKg}
                onChange={(e) => setWeightGoalKg(e.target.value)}
                placeholder="e.g. 72.0"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
          </div>
        </div>

        {/* Chronic conditions */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <h2 className="font-semibold text-gray-800 mb-4">Chronic Conditions</h2>
          <div className="flex flex-wrap gap-2">
            {CHRONIC_CONDITIONS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => toggleCondition(c)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                  (profile.chronic_conditions || []).includes(c)
                    ? 'bg-emerald-600 text-white border-emerald-600'
                    : 'bg-white text-gray-600 border-gray-300 hover:border-emerald-400'
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        {/* Medications */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <h2 className="font-semibold text-gray-800 mb-4">Current Medications</h2>
          <div className="flex gap-2 mb-3">
            <input
              type="text"
              value={newMed}
              onChange={(e) => setNewMed(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addMed())}
              placeholder="e.g. Metformin 500mg"
              className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
            <button type="button" onClick={addMed} className="px-4 py-2.5 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700">
              Add
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {(profile.medications || []).map((m: string, i: number) => (
              <span key={i} className="flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm">
                {m}
                <button type="button" onClick={() => removeMed(i)} className="text-blue-400 hover:text-red-500 ml-1">×</button>
              </span>
            ))}
          </div>
        </div>

        {/* Allergies */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <h2 className="font-semibold text-gray-800 mb-4">Allergies</h2>
          <div className="flex gap-2 mb-3">
            <input
              type="text"
              value={newAllergy}
              onChange={(e) => setNewAllergy(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addAllergy())}
              placeholder="e.g. Penicillin, Peanuts"
              className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
            <button type="button" onClick={addAllergy} className="px-4 py-2.5 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700">
              Add
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {(profile.allergies || []).map((a: string, i: number) => (
              <span key={i} className="flex items-center gap-1 px-3 py-1 bg-red-50 text-red-700 rounded-full text-sm">
                {a}
                <button type="button" onClick={() => removeAllergy(i)} className="text-red-400 hover:text-red-600 ml-1">×</button>
              </span>
            ))}
          </div>
        </div>

        {/* Family History */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <h2 className="font-semibold text-gray-800 mb-4">Family History</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {Object.entries(profile.family_history || {}).map(([key, val]) => (
              <label key={key} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!val}
                  onChange={(e) => setProfile((p: any) => ({
                    ...p,
                    family_history: { ...p.family_history, [key]: e.target.checked },
                  }))}
                  className="w-4 h-4 accent-emerald-600"
                />
                <span className="text-sm text-gray-700 capitalize">{key.replace(/_/g, ' ')}</span>
              </label>
            ))}
          </div>
        </div>

        <button
          type="submit"
          disabled={saving}
          className="w-full py-3 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 disabled:opacity-50 transition-colors"
        >
          {saving ? 'Saving...' : 'Save Health Profile'}
        </button>
      </form>
    </div>
  );
}
